import React, { forwardRef } from "react";

import { useSlotProps } from "@mui/base/utils";
import { Box, IconButton } from "@mui/material";
import { PickersShortcutsItem } from "@mui/x-date-pickers";
import {
  DateRange,
  SingleInputDateRangeFieldProps,
} from "@mui/x-date-pickers-pro";
import { DateRangePicker as MUIDateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { useSingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField/useSingleInputDateRangeField";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { Dayjs } from "dayjs";
import { dayjsTZ } from "helpers";
import { rem } from "utils";

import { useDateRangePicker } from "./DateRangePicker.hooks";
import {
  DateRangePickerFieldProps,
  DateRangePickerProps,
  DateRangePickerReference,
} from "./DateRangePicker.types";
import { CalendarIcon } from "../Icon";
import TextField from "../TextField/TextField";

const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs | null>>[] = [
  {
    label: "This Week",
    getValue: () => {
      const today = dayjsTZ();
      return [today.startOf("week"), today.endOf("week")];
    },
  },
  {
    label: "Last Week",
    getValue: () => {
      const today = dayjsTZ();
      const prevWeek = today.subtract(7, "day");
      return [prevWeek.startOf("week"), prevWeek.endOf("week")];
    },
  },
  {
    label: "Last 7 Days",
    getValue: () => {
      const today = dayjsTZ();
      return [today.subtract(7, "day"), today];
    },
  },
  {
    label: "Current Month",
    getValue: () => {
      const today = dayjsTZ();
      return [today.startOf("month"), today.endOf("month")];
    },
  },
  {
    label: "Last Month",
    getValue: () => {
      const today = dayjsTZ();
      const endOfLastMonth = today.startOf("month").subtract(1, "day");
      return [endOfLastMonth.startOf("month"), endOfLastMonth];
    },
  },
  {
    label: "Current Year",
    getValue: () => {
      const today = dayjsTZ();
      return [today.startOf("year"), today.endOf("year")];
    },
  },
  {
    label: "Last Year",
    getValue: () => {
      const today = dayjsTZ();
      const endOfLastYear = today.startOf("year").subtract(1, "day");
      return [endOfLastYear.startOf("year"), endOfLastYear];
    },
  },
  { label: "Reset", getValue: () => [null, null] },
];

const DateRangePickerField: React.FC<DateRangePickerFieldProps> = (props) => {
  const { slots, slotProps, readOnly, onClick, ...other } = props;

  const {
    inputRef: externalInputRef,
    ...textFieldProps
  }: SingleInputDateRangeFieldProps<Dayjs> = useSlotProps({
    elementType: null as never,
    externalSlotProps: slotProps?.textField,
    externalForwardedProps: other,
    ownerState: props,
  });

  const {
    onChange,
    InputProps,
    ref: textfieldRef,
    label,
    value,
    ...innerResponse
  } = useSingleInputDateRangeField<Dayjs, typeof textFieldProps>({
    props: textFieldProps,
    inputRef: externalInputRef,
  });

  return (
    <TextField
      // {...innerResponse}
      label={label}
      value={value}
      inputRef={textfieldRef}
      onChangeEvent={onChange}
      onClick={onClick}
      readOnly
      InputProps={{
        ...(InputProps ?? {}),
        startAdornment: (
          <IconButton
            disabled={textFieldProps.disabled || readOnly}
            sx={{ marginX: rem(-10) }}
            onClick={
              onClick as unknown as React.MouseEventHandler<HTMLButtonElement>
            }
          >
            <CalendarIcon fontSize="small" sx={{ color: gray[700] }} />
          </IconButton>
        ),
        endAdornment: null, // Hide default "Calendar" icon of MUI Date Picker
      }}
    />
  );
};

const DateRangePicker = forwardRef<
  DateRangePickerReference,
  DateRangePickerProps
>(
  (
    {
      label,
      defaultValue,
      value,
      placeholder,
      required,
      helperText,
      error,
      onChange,
      ...innerProps
    },
    ref
  ) => {
    const {
      rootRef,
      valueState,
      openCalendar,
      onChangeHandler,
      onCalendarCloseHandler,
      toggleCalendar,
    } = useDateRangePicker({
      ref,
      defaultValue,
      value,
      onChange,
    });

    return (
      <Box ref={rootRef}>
        <MUIDateRangePicker
          ref={ref as React.Ref<HTMLDivElement>}
          value={valueState}
          open={openCalendar}
          onChange={onChangeHandler}
          onClose={onCalendarCloseHandler}
          slots={{ field: DateRangePickerField }}
          slotProps={{
            popper: {
              anchorEl: rootRef.current,
              sx: {
                ".MuiPickersPopper-paper": {
                  boxShadow: shadows.lg,
                },
              },
            },
            shortcuts: {
              items: shortcutsItems as unknown as PickersShortcutsItem<
                DateRange<Dayjs | null>
              >[],
            },
            textField: {
              label,
              required,
              placeholder,
              helperText,
              error,
            },
            field: {
              readOnly: innerProps.readOnly,
              onClick: toggleCalendar,
            } as never,
          }}
          {...innerProps}
        />
      </Box>
    );
  }
);

export default DateRangePicker;
